/**
 * @module Format Balances
 */
import { formatUnits } from "viem";

import type { KeybanClient, KeybanToken } from "~/index";

/**
 * Represents a balance with optional metadata.
 * @example
 * const balance: Balance = { raw: 1_000_000_000_000_000_000n, decimals: 18, symbol: "ETH", isNative: true };
 * const tokenBalance: Balance = { raw: 500_000_000_000_000_000n, decimals: 18, symbol: "DAI" };
 */
export type Balance = {
  /**  The raw balance value. */
  raw: string | bigint;
  /** The number of decimal places for the balance. */
  decimals?: number;
  /** The symbol of the currency. */
  symbol?: string;
  /** Indicates if the balance is in the native currency. */
  isNative?: boolean;
  /** Indicates if the balance is used for fees. */
  isFees?: boolean;
};

/**
 * Format a balance using the client's native/fee units or the token metadata.
 * @param client - The Keyban client providing network native currency and fee units.
 * @param balance - Raw amount and optional hints: decimals/symbol/isNative/isFees.
 * @param [token] - Optional token metadata used when `isNative`/`isFees` are false.
 * @returns A formatted string with decimals applied and symbol when available.
 * @example
 * import { KeybanClient, KeybanNetwork, formatBalance } from "@keyban/sdk-base";
 * const client = new KeybanClient({ apiUrl: "https://api.prod.keyban.io", appId: "APP", network: KeybanNetwork.EthereumAnvil });
 * const native = { raw: 1_000_000_000_000_000_000n, isNative: true };
 * console.log(formatBalance(client, native)); // "1 ETH"
 * const token = { id: "1", type: "ERC20", name: "Tether", symbol: "USDT", decimals: 6 } as KeybanToken;
 * console.log(formatBalance(client, { raw: 1_000_000n }, token)); // "1 USDT"
 * console.log(formatBalance(client, { raw: 1_000n, isFees: true })); // e.g. "0.000001 gwei"
 */
export function formatBalance(
  client: KeybanClient,
  balance: Balance,
  token?: KeybanToken,
): string {
  let decimals = balance.decimals;
  let symbol = balance.symbol;
  if (balance.isFees) {
    decimals = client.feesUnit.decimals;
    symbol = client.feesUnit.symbol;
  } else if (balance.isNative) {
    decimals = client.nativeCurrency.decimals;
    symbol = client.nativeCurrency.symbol;
  } else if (token) {
    decimals = token.decimals ?? undefined;
    symbol = token.symbol ?? undefined;
  }
  decimals = decimals ?? 0;

  const fmt = formatUnits(
    typeof balance.raw === "bigint" ? balance.raw : BigInt(balance.raw),
    decimals,
  );
  return symbol ? `${fmt} ${symbol}` : fmt;
}
